import type { components } from '@pba/types-openapi-node';

export type Attendee = {
  email?: string;
  id: string;
  name: string;
  partstat: PrincipalCalendarAttendeePartstat;
};

export type Duration = {
  days?: number;
  hours?: number;
  minutes?: number;
  months?: number;
  seconds?: number;
  weeks?: number;
  years?: number;
};

export type Organizer = {
  email: string;
  id: string;
  name: string;
};

export type PrincipalCalendarAttendeePartstat = components['schemas']['CalendarAttendee']['partstat'];

export type PrincipalCalendarCategory =
  | 'CALL'
  | 'CLINICAL_REVIEW'
  | 'REMINDER'
  | 'TELEHEALTH_VISIT'
  | 'VIDEO_CONFERENCE'
  | 'WEBINAR';

export type PrincipalCalendarEvent = {
  attendees?: Attendee[];
  categories: PrincipalCalendarCategory[];
  duration?: Duration;
  endTime: Date;
  goingCount: number;
  hasRecording: boolean;
  id: string;
  isAllDay: boolean;
  organizer: Organizer;
  properties: Property[];
  startTime: Date;
  status: PrincipalCalendarEventStatus;
  summary: string;
};

export type PrincipalCalendarEventStatus = components['schemas']['CalendarEvent']['status'];

export type Property = {
  name: string;
  value: string;
};

const getGoingCount = (properties: Array<components['schemas']['CalendarXProp']>): number => {
  const goingCountProperty = properties.find((property) => property.name === 'GOING-COUNT')?.value;
  return Number.isNaN(Number(goingCountProperty)) ? 0 : Number(goingCountProperty);
};

export const createPrincipalCalendarEvent = (data: components['schemas']['CalendarEvent']): PrincipalCalendarEvent => ({
  attendees: data.attendees?.map((attendee) => createAttendee(attendee)),
  categories: data.categories,
  endTime: new Date(data.end),
  goingCount: getGoingCount(data.xProps),
  hasRecording: data.xProps.some((property) => property.name === 'RECORDING-URL'),
  id: data.uid,
  isAllDay: data.xProps.some((property) => property.name === 'ALL-DAY'),
  organizer: createOrganizer(data.organizer),
  properties: data.xProps.map((property) => createProperty(property)),
  startTime: new Date(data.start),
  status: data.status,
  summary: data.summary,
});

const createAttendee = (attendee: components['schemas']['CalendarAttendee']): Attendee => ({
  email: attendee.address,
  id: attendee.dir,
  name: attendee.cn,
  partstat: attendee.partstat,
});

const createOrganizer = (organizer: components['schemas']['CalendarAttendee']): Organizer => ({
  email: organizer.address,
  id: organizer.dir,
  name: organizer.cn,
});

const createProperty = (property: components['schemas']['CalendarXProp']): Property => ({
  name: property.name,
  value: property.value,
});
